import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Phone from '@material-ui/icons/PhoneOutlined';
import Business from '@material-ui/icons/Business';
import { TextCell, AccountCell, LinkCell, DepartmentsCell } from 'components/Cells/Cells';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { translate } from 'components/Translation';
import { capitalize } from 'utils/converters/capitalize';
import { throwError } from 'rxjs';
import { showLoadingIndicator, hideLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import { exportToXLS } from 'utils/export/export';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/components/customAvatarStyle';
import Account from '@material-ui/icons/AccountCircleOutlined';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import { IFilter } from 'components/CustomFilter/CustomFilter';
import { Column } from 'react-table';
import { setFilters } from 'redux/modules/numbers/actions';
import { useSelector } from 'react-redux';
import { filtersSelector } from 'redux/modules/numbers/selectors';
import useParseFixFbbNumbers from 'commons/hooks/numbers';
import { numberDetailsPath } from 'utils/paths/numberDetailsPath';
import {
	AAMALIPLUS,
	AAMALI_PLUS_ACCOUNT_CATEGORY,
	FBB_ACCOUNT_CATEGORIES,
	AAMALI_PLUS_ACCOUNT_CATEGORIES,
} from '../constants';
import { numberListFilters } from 'views/Dashboard/constants';

// @ts-ignore
const useStyles = makeStyles(styles);

const NumbersList = () => {
	const classes = useStyles();
	const [numbers, setNumbers] = useState<SingleAccount[]>([]);
	const [selected, setSelected] = useState([]);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const dispatch = useDispatch();
	const userPathName: string = `/dashboard/users`;
	const filters = useSelector(filtersSelector);
	const hasSelectedAllAccounts: boolean = !selectedAccount || selectedAccount?.id === null;

	useEffect(() => {
		if (window?.location?.search) {
			const urlParams = new URLSearchParams(window.location.search);
			const serviceType = urlParams.get('service_type');

			if (serviceType === numberListFilters?.[0]) {
				dispatch(
					setFilters({
						type: 'service',
						value: numberListFilters,
					})
				);
			}
		}
	}, [dispatch, window?.location?.search]);

	const departmentsMap = useMemo(
		() => departments.reduce((acc, department) => ({ ...acc, [department.deptId]: department }), {}),
		[departments]
	);
	const accountsMap = useMemo(
		() => accounts.reduce((acc, account) => ({ ...acc, [account.accountId]: account }), {}),
		[accounts]
	);
	const parsedNumbers = (useParseFixFbbNumbers(numbers) as unknown[]) as SingleAccount[];

	const filteredNumbers = useMemo(() => {
		const types: [string, string | null, string | null][] = filters.service.map(
			(type: types.redux.numbers.serviceType) => {
				switch (type) {
					case 'mobile-postpaid':
						return ['MOBILE', 'POSTPAID', null];
					case 'mobile-prepaid':
						return ['MOBILE', 'PREPAID', null];
					case 'fixed-line':
						return ['FIX', null, null];
					case 'fbb':
						return ['INTERNET', null, FBB_ACCOUNT_CATEGORIES];
					case 'wfbb':
						return ['WFBB', null, null];
					case 'aamali':
						return ['INTERNET', null, AAMALI_PLUS_ACCOUNT_CATEGORIES];
					default:
						return [null, null, null];
				}
			}
		);
		const _searchInputValue = searchInputValue.toLowerCase();

		return parsedNumbers.filter((number) => {
			return [
				[selectedAccount?.id != null, number.accountId === selectedAccount?.id],
				[
					filters.service.length > 0,
					types.some(
						([accountType, billingType, accountCategory]) =>
							number.accountType === accountType &&
							(billingType === null || billingType === number.billingType) &&
							(accountCategory === null ||
								(accountCategory.length &&
									number.accountCategory &&
									accountCategory.includes(number.accountCategory)))
					),
				],
				[
					filters.userType.length > 0,
					filters.userType.some((userType: string) =>
						userType === ''
							? !number.userType || number.userType === userType
							: number.userType === userType
					),
				],
				[
					filters.department.length > 0,
					filters.department.some((deptId: string) =>
						deptId === '' ? !number.deptId || deptId === number.deptId : deptId === number.deptId
					),
				],
				[
					_searchInputValue !== '',
					['accountNum', 'login']
						.filter((field) => !!number[field])
						.some((field) => number[field].toLowerCase().includes(_searchInputValue)),
				],
			]
				.filter(([check, _]) => check)
				.every(([_, isTrue]) => isTrue);
		});
	}, [searchInputValue, selectedAccount, parsedNumbers, filters]);

	const exportToXls = useCallback(() => {
		const preparedData = (selected.length === 0
			? filteredNumbers
			: selected.map((index) => filteredNumbers[index])
		).map((number) => {
			const account = accountsMap[number.accountId];
			const department = departmentsMap[number.deptId];
			return {
				Number: number.accountNum,
				'User name': number.login || '',
				'Service type': `${capitalize(number.accountType)} ${number.billingType.toLowerCase()}`,
				'Organization name': account?.accountName,
				Department: department?.name || '',
			};
		});

		exportToXLS(preparedData, `numbers ${moment().format('LLL')}`);
	}, [selected, filteredNumbers, accountsMap, departmentsMap]);

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		AccountsService.getCorpUserNumbersWithChilds()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe(
				({ accounts: numbers }) => {
					if (numbers) {
						numbers = Array.isArray(numbers) ? numbers : [numbers];
						setNumbers(numbers);
					}
				},
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch]);

	useEffect(() => {
		DepartmentsService.getDepartmentsForCurrentUser().subscribe(({ departments }) => {
			setDepartments(departments.filter((department) => department.deptId));
		});
		AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
		});
	}, []);

	useEffect(() => setSelected([]), [filteredNumbers]);

	const accountDetailsPath = useCallback((account) => {
		return numberDetailsPath(
			account.accountNum,
			account.bAccountId,
			account.accountType,
			account.billingType,
			account.upAccountId,
			account.accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY,
			AAMALI_PLUS_ACCOUNT_CATEGORY
		);
	}, []);

	const accountDepartments = useMemo(() => {
		let departmentsList = departments
			.filter(({ accountId }) => !!selectedAccount && accountId === selectedAccount.id)
			.map((department) => ({
				title: department.name,
				value: department.deptId,
			}));
		return [...departmentsList, { title: translate('numbers.details.notAssigned'), value: '' }];
	}, [departments, selectedAccount]);

	const serviceType = (number: SingleAccount): string => {
		const { accountType, billingType, accountCategory } = number;
		if (accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY) {
			return translate('aamali.plus.service');
		}
		if (accountType === 'INTERNET') {
			return 'FBB';
		}
		if (accountType === 'FIX') {
			return 'Fixed Line';
		}
		return `${capitalize(accountType)} ${capitalize(billingType)}`;
	};

	const userDetailsPath = (userId: string): string => {
		return `${userPathName}/${userId}`;
	};

	const onFilterChange = useCallback(
		(name, value) => {
			dispatch(setFilters({ type: name, value }));
		},
		[dispatch]
	);

	return (
		<div style={{ width: '100%' }}>
			<AccountFilterTable
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				onFilterChange={onFilterChange}
				filterHeader={translate('numbersList')}
				filtersValue={filters}
				filters={
					[
						{
							title: translate('numbers.serviceType'),
							name: 'service',
							type: 'multiple',
							//aamali is aamaliPlus
							elements: ['mobile-postpaid', 'mobile-prepaid', 'fixed-line', 'fbb', 'wfbb', 'aamali'].map(
								(value) => ({
									title: translate(`numbers.${value}`),
									value,
								})
							),
						},
						{
							title: translate('user.type'),
							name: 'userType',
							type: 'multiple',
							elements: ['ENDUSER', 'MANAGER', 'ADMIN', 'NONE'].map((value) => ({
								title: translate(`numbers.user.${value}`),
								value: value === 'NONE' ? '' : value,
							})),
						},
						selectedAccount &&
							accountDepartments.length > 0 && {
								title: translate('numbers.department'),
								name: 'department',
								type: 'multiple',
								elements: accountDepartments,
							},
					].filter(Boolean) as IFilter[]
				}
				setSearchInputValue={(value) => setSearchInputValue(value)}
				searchInputValue={searchInputValue}
				exportToXLS={exportToXls}
				data={filteredNumbers}
				onSelect={(value) => setSelected(value)}
				selectPanel={{
					selectedElements: selected.length,
					downloadMenuItems: [
						{
							name: 'XSLX',
							onClick: exportToXls,
						},
					],
				}}
				loading={loading}
				selected={selected}
				columns={
					[
						{
							Header: translate('table.phone.number'),
							// eslint-disable-next-line react/prop-types
							Cell: ({ original }: { original: SingleAccount }) => (
								<LinkCell
									path={accountDetailsPath(original)}
									icon={<Phone />}
									text={original.accountNum}
									/**
								added to show aamali+services, in case of micro bundle when the route changes
								accountCategory is used as an identifier for microbundle
								Props are optional in LinkCell and account category is passed in state of history
									*/
									props={{ accountCategory: original.accountCategory }}
								/>
							),
							accessor: 'accountNum',
						},
						{
							Header: translate('table.user.name'),
							Cell: ({ original }: { original: SingleAccount }) => (
								<LinkCell
									path={userDetailsPath(original.userId)}
									icon={<Account />}
									text={original.login}
									/**
								added to show aamali+services, in case of micro bundle when the route changes
								accountCategory is used as an identifier for microbundle
								Props are optional in LinkCell and account category is passed in state of history
									*/
									props={{ accountCategory: original.accountCategory }}
								/>
							),
							accessor: 'login',
						},
						{
							Header: translate('table.service.type'),
							Cell: ({ original }: { original: SingleAccount }) => (
								<TextCell text={serviceType(original)} />
							),
							accessor: (row: any) => `${row.accountType} ${row.billingType}`,
							id: 'serviceType',
						},
						hasSelectedAllAccounts && {
							Header: translate('table.organization.name'),
							Cell: ({ original }: { original: SingleAccount }) => {
								const account = accountsMap[original.accountId];
								return account ? (
									<AccountCell
										text={account.accountName}
										icon={
											<CustomAvatar
												accountId={account.accountId}
												alt="Company Logo"
												placeholderIcon={<Business />}
												customClasses={classes.inlineIcon}
											/>
										}
									/>
								) : null;
							},
							id: 'account.accountName',
							accessor: (row: any) => accountsMap[row.accountId],
						},
						{
							Header: translate('numbers.department'),
							id: 'department',
							Cell: ({ original }: { original: SingleAccount }) => {
								const { deptId } = original;
								const dept = departmentsMap[deptId];
								return (
									<DepartmentsCell
										departments={
											dept
												? ([
														{
															name: dept.name,
															uiColor: dept.uiColor,
															deptId: dept.deptId,
														},
												  ] as SingleDepartment[])
												: []
										}
									/>
								);
							},
							accessor: (row: any) => departmentsMap[row.deptId]?.name,
						},
					].filter(Boolean) as Column<any>[]
				}
			/>
		</div>
	);
};

export default NumbersList;
